import { Box, Typography, Table, TableBody, TableCell, TableRow, Paper } from '@mui/material';
import { format, isValid, parse } from 'date-fns';

const Step1 = ({ formik }) => {
  const { lease_details = {}, property_details = {}, tenants = [] } = formik.values;

  const {
    start_date,
    end_date,
    basic_rent,
    pets = [],
    parking = [],
    storage = [],
    pro_ration,
    telus,
    incentives
  } = lease_details;

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[3]}-${match[2]}-${match[1]}` : 'Invalid Date';
  };

  const getOrdinal = (day) => {
    if (day > 3 && day < 21) return `${day}th`;
    switch (day % 10) {
      case 1: return `${day}st`;
      case 2: return `${day}nd`;
      case 3: return `${day}rd`;
      default: return `${day}th`;
    }
  };

  const formatDateWithOrdinal = (dateString) => {
    if (!dateString) return 'Click to select date';
    const cleanDateStr = dateString.split('T')[0];
    const date = parse(cleanDateStr, 'yyyy-MM-dd', new Date());
    if (!isValid(date)) return 'Click to select date';
    const month = format(date, 'MMMM');
    const dayWithOrdinal = getOrdinal(date.getDate());
    const year = format(date, 'yyyy');
    return `${month} ${dayWithOrdinal}, ${year}`;
  };

  // Calculate fees
  const calculatePetFees = () => {
    if (!pets || pets.length === 0) return 0;
    return pets.reduce((sum, pet) => {
      const fee = parseFloat(pet.total_fee || 0);
      return sum + fee;
    }, 0);
  };

  const calculateParkingFees = () => {
    if (!parking || parking.length === 0) return 0;
    return parking.reduce((sum, item) => {
      const fee = parseFloat(item.total_fee || 0);
      return sum + fee;
    }, 0);
  };

  const calculateStorageFees = () => {
    if (!storage || storage.length === 0) return 0;
    return storage.reduce((sum, item) => {
      const fee = parseFloat(item.total_fee || 0);
      return sum + fee;
    }, 0);
  };

  const calculateTotalRent = () => {
    const br = parseFloat(basic_rent || 0);
    const pet = calculatePetFees();
    const park = calculateParkingFees();
    const store = calculateStorageFees();
    const telusCharge = parseFloat(telus?.charges || 0);
    const total = br + pet + park + store + telusCharge;
    return `${formatCurrency(total)}`;
  };

  const formatPetInfo = () => {
    if (!pets || pets.length === 0) return "Not Applicable";

    const petDetails = pets.map(pet => {
      const type = pet.category
        ? pet.category.charAt(0).toUpperCase() + pet.category.slice(1)
        : 'Pet';
      const fee = parseFloat(pet.total_fee || 0);

      return `${type}: ${formatCurrency(fee)}`;
    });

    if (petDetails.length === 2) {
      return petDetails.join(' and ');
    } else if (petDetails.length > 2) {
      return `${petDetails.slice(0, -1).join(', ')} and ${petDetails[petDetails.length - 1]}`;
    }

    return petDetails[0] || "Not Applicable";
  };

  const formatParkingInfo = () => {
    if (!parking || parking.length === 0) return "Not Applicable";

    const parkingDetails = parking.map(item => {
      const type = item.parking_type
        ? item.parking_type.charAt(0).toUpperCase() + item.parking_type.slice(1)
        : 'Parking';
      const fee = parseFloat(item.total_fee || 0);

      return `${type}: ${formatCurrency(fee)}`;
    });

    if (parkingDetails.length === 2) {
      return parkingDetails.join(' and ');
    } else if (parkingDetails.length > 2) {
      return `${parkingDetails.slice(0, -1).join(', ')} and ${parkingDetails[parkingDetails.length - 1]}`;
    }

    return parkingDetails[0] || "Not Applicable";
  };

  const formatStorageInfo = () => {
    if (!storage || storage.length === 0) return "Not Applicable";

    const storageDetails = storage.map(item => {
      const count = parseInt(item.no_of_slots || 0);
      const fee = parseFloat(item.total_fee || 0);

      return `Storage (${count} slot${count > 1 ? 's' : ''}): ${formatCurrency(fee)}`;
    });

    if (storageDetails.length === 2) {
      return storageDetails.join(' and ');
    } else if (storageDetails.length > 2) {
      return `${storageDetails.slice(0, -1).join(', ')} and ${storageDetails[storageDetails.length - 1]}`;
    }

    return storageDetails[0] || "Not Applicable";
  };

  const formatProRationInfo = () => {
    if (!pro_ration?.date_of_early_move_in) return "Not Applicable";
    const standard = parseFloat(pro_ration.amount_applicable || 0);
    const actual = parseFloat(pro_ration.amount_to_be_charged || 0);
    return `Early Move-In ${formatDateWithOrdinal(pro_ration.date_of_early_move_in)}: Prorated Amt. ${formatCurrency(standard)}, Actual Amt. ${formatCurrency(actual)}`;
  };

  const formatTelusInfo = () => {
    if (!telus) return "Not included";

    return telus.included
      ? `Included`
      : `Not Included${telus.charges ? ` : ${formatCurrency(telus.charges)}` : ''}`;
  };

  const formatIncentivesInfo = () => {
    if (!incentives) return 'None';
    return Array.isArray(incentives) ? incentives.join(', ') : incentives;
  };

  return (
    <Box className="lease-form-step1">
      <Typography variant="h6" align="center" gutterBottom className="form-title">
        RESIDENTIAL TENANCY AGREEMENT
      </Typography>

      <Typography variant="body2" align="center" className="subtitle">
        (made in duplicate) Between
      </Typography>

      <Typography variant="body1" align="center" className="landlord-name" sx={{ mt: 2, mb: 3 }}>
        {property_details.partnership_label} / {property_details.landlord} (the "Landlord") – and –
      </Typography>

      {/* Tenant Info Table */}
      <Paper variant="outlined" className="unit-details-table">
        <Table size="small">
          <TableBody>
            {tenants.map((tenant, index) => (
              <TableRow key={tenant.id || index}>
                <TableCell sx={{ width: '35%', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Full Name
                </TableCell>
                <TableCell sx={{ width: '65%' }}>
                  {tenant.name || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>


      <Typography variant="body2" align="center" className="tenant-reference" sx={{ mt: 1 }}>
        (Individually or collectively referred to as the "Tenant")
      </Typography>

      {/* Agreement Title */}
      <Typography variant="body2" align="center" className="agreement-note" sx={{ mt: 1, mb: 1 }}>
        THE TENANCY CREATED BY THIS AGREEMENT IS GOVERNED BY THE <b>RESIDENTIAL TENANCIES ACT</b> (THE "ACT"),
        AND IF THERE IS A CONFLICT BETWEEN THE AGREEMENT AND THE ACT, THE ACT PREVAILS.
      </Typography>

      <Typography variant="body1" component="div" className="lease-text">
        <b>PREMISES:</b> The Landlord hereby leases to the Tenant and the Tenant hereby leases from the Landlord the following residential premises described as follows;
      </Typography>

      {/* Premises Info Table */}
      <Paper variant="outlined" className="unit-details-table">
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell className="label">Building</TableCell>
              <TableCell>{property_details.property_name || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">Unit Number</TableCell>
              <TableCell>{property_details.unit_no || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">Address</TableCell>
              <TableCell>{property_details.address || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">City, Province</TableCell>
              <TableCell>
                {property_details.city || '-'}{property_details.province ? ` - ${property_details.province}` : ''}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">Postal Code</TableCell>
              <TableCell>{property_details.postal_code || '-'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      {/* Sections */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">1. TERM & NOTICE</Typography>
        <Typography variant="body2" className="section-content">
          This agreement is for a fixed term, commencing at 12 o'clock noon on{' '}
          <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {formatDateWithOrdinal(start_date)}
          </Box>
          , ending at 12 o'clock noon on{' '}
          <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {formatDateWithOrdinal(end_date)}
          </Box>
        </Typography>

      </Box>

      <Box className="section">
        <Typography variant="subtitle2" className="section-title">2. RENT</Typography>
        <Typography variant="body2" className="section-content">
          The tenant shall pay to the landlord without any deductions whatsoever as the landlord shall direct
          from time to time a monthly rental sum [hereafter called "rent"] of:
        </Typography>
      </Box>

      {/* Rent Info Table with Detailed Breakdowns */}
      <Paper variant="outlined" className="lease-details-table" sx={{ mt: 2 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell className="label">Commencement Date:</TableCell>
              <TableCell>{formatDateForDisplay(start_date)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">Monthly Basic Rent:</TableCell>
              <TableCell>{formatCurrency(basic_rent || '0')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">Monthly Pet Fee:</TableCell>
              <TableCell className="detailed-cell">{formatPetInfo()}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">Monthly Parking Fee:</TableCell>
              <TableCell className="detailed-cell">{formatParkingInfo()}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">Monthly Storage Fee:</TableCell>
              <TableCell className="detailed-cell">{formatStorageInfo()}</TableCell>
            </TableRow>
            <TableRow className="total-rent-row">
              <TableCell className="total">TOTAL MONTHLY RENT:</TableCell>
              <TableCell className="total">{calculateTotalRent()}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">Pro-ration of first month, if applicable:</TableCell>
              <TableCell className="detailed-cell">{formatProRationInfo()}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">Telus:</TableCell>
              <TableCell>{formatTelusInfo()}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="label">Incentives:</TableCell>
              <TableCell>
                {formatIncentivesInfo()}
                {incentives && (
                  <Typography className="incentives-note" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                    <em>Incentives are subject to terms and conditions of this agreement.</em>
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
      <Box className="caption">
        <Typography variant="body2" className="caption">
          Rent is due in full no later than the 1<sup>st</sup> day of each month. <br />
          When two or more persons comprise the tenant for the purposes of this agreement, the landlord may collect the rent due to the landlord pursuant to the agreement from anyone, some or all of them. The tenant shall pay the rent reserved, without demand, at the times and in the manner specified here in provided without deduction, abatement or set-off.
        </Typography>
      </Box>

      <Box className="section">
        <Typography variant="subtitle2" className="section-title">3. NOTICE OF EARLY LEASE TERMINATION</Typography>
        <Typography variant="body2" className="section-content" sx={{ backgroundColor: '#ffff99', p: 1, borderRadius: 1 }}>
          Subject to termination in accordance with this Agreement or the Act, the Tenant ends
          the fixed-term tenancy prior to the Expiry Date, the Landlord may treat this Agreement
          as abandoned in accordance with the Act. In such an event, the Tenant shall pay to the
          Landlord a lease break fee equivalent to two (2) months' rent. This amount is payable
          in addition to any other amounts owed by the Tenant, including but not limited to unpaid
          rent, or the reasonable cost of repairing any damage to the Premises or Residential
          Property (excluding fair wear and tear).
        </Typography>
      </Box>
    </Box>
  );
};

export { Step1 };